import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { Paprika, PaprikaFreshness } from '../../styles/theme';
import type { IngredientPantryStatus } from '../../services/ingredientStatusService';

interface StockMarkProps {
  status: IngredientPantryStatus;
  onPress?: () => void;
}

/**
 * Display-only pantry status mark on an ingredient row: terracotta cart = to
 * buy, green check = already in the pantry (staples included), purple question
 * = the pantry MIGHT cover it. Tapping it opens the pantry-correction strip —
 * it is never a checkbox.
 *
 * The question mark is not decoration. This component used to treat everything
 * that was not 'buy' as 'have', so when the pantry held something merely
 * related — five limes against a recipe wanting lime juice — the row showed a
 * confident green check. Reusing the `review` tone keeps that meaning where the
 * pantry already puts it: purple is what this app uses for "we are not sure".
 */
export const StockMark: React.FC<StockMarkProps> = ({ status, onPress }) => {
  const isBuy = status === 'buy';
  const isMaybe = status === 'probably';
  const inner = (
    <View style={[styles.mark, isBuy ? styles.buy : isMaybe ? styles.maybe : styles.have]}>
      <Feather
        name={isBuy ? 'shopping-cart' : isMaybe ? 'help-circle' : 'check'}
        size={13}
        color={isBuy ? Paprika.accent : isMaybe ? PaprikaFreshness.review.text : Paprika.success}
      />
    </View>
  );

  if (!onPress) return inner;

  return (
    <TouchableOpacity
      testID="stock-mark"
      onPress={onPress}
      activeOpacity={0.6}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      accessibilityRole="button"
    >
      {inner}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  mark: {
    width: 24,
    height: 24,
    borderRadius: 7,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buy: {
    backgroundColor: Paprika.brandTint,
  },
  have: {
    backgroundColor: Paprika.successSoft,
  },
  maybe: {
    backgroundColor: PaprikaFreshness.review.bg,
  },
});

export default StockMark;
